import { z } from 'zod'
import { queryBoolean } from './zod-query'

// ─── Enums ───────────────────────────────────────────────────────────────────

// NB: de enum-waarde `MAPBOX` is om historische reden zo benoemd. Sinds de
// Google-migratie (2026-05) staat die waarde voor "extern berekend via de
// actieve route-provider" — in productie is dat Google Maps. De DB-enum is
// niet hernoemd om bestaande rijen niet te breken; de UI-label gebruikt wel
// de Google-naam (zie DISTANCE_CACHE_SOURCE_LABELS).
export const DISTANCE_CACHE_SOURCE = ['MAPBOX', 'PDOK', 'HANDMATIG', 'ONBEKEND'] as const
export type DistanceCacheSource = (typeof DISTANCE_CACHE_SOURCE)[number]

export const DISTANCE_CACHE_SOURCE_LABELS: Record<DistanceCacheSource, string> = {
  MAPBOX:    'Google Maps',
  PDOK:      'PDOK',
  HANDMATIG: 'Handmatig',
  ONBEKEND:  'Onbekend',
}

// ─── DTO's ───────────────────────────────────────────────────────────────────

/** Resultaat van een single-lookup. `cached: false` betekent vers gefetcht. */
export interface DistanceLookupResult {
  kilometers: number       // enkele richting (fractioneel)
  minutes: number          // enkele richting (afgerond, legacy/weergave)
  durationSeconds: number  // enkele richting, precies
  source: DistanceCacheSource
  fetchedAt: string        // ISO
  cached: boolean          // true = uit DB-cache, false = vers gefetcht
}

export interface DistanceLookupBatchResultRow {
  to: { postcode: string; houseNumber: string }
  result: DistanceLookupResult | null  // null bij fetch-error
}

export interface DistanceLookupBatchResult {
  results: DistanceLookupBatchResultRow[]
}

// ─── Postcode-labels (FO-027 Deel B) ─────────────────────────────────────────
//
// In het reisafstanden-overzicht krijgt elke postcode een label: de monteur
// (woon-/start-postcode) of de klant (bestemmings-/locatie-postcode). Postcode +
// naam is interne info; de resolutie gebeurt server-side achter de
// REISAFSTANDEN-gate, dus er is geen extra blootstelling.
export const POSTCODE_LABEL_TYPES = ['MONTEUR', 'KLANT'] as const
export type PostcodeLabelType = (typeof POSTCODE_LABEL_TYPES)[number]

export const POSTCODE_LABEL_TYPE_LABELS: Record<PostcodeLabelType, string> = {
  MONTEUR: 'monteur',
  KLANT: 'klant',
}

export interface PostcodeLabel {
  type: PostcodeLabelType
  naam: string
  /** Doel voor doorklikken: medewerker-id (MONTEUR) of klant-id (KLANT). */
  id: string
}

export interface DistanceCacheRowDTO {
  id: string
  from: { postcode: string; houseNumber: string }
  to: { postcode: string; houseNumber: string }
  kilometers: number
  minutes: number
  source: DistanceCacheSource
  fetchedAt: string
  ageDays: number
  isStale: boolean
  // FO-027 Deel B: wie hoort bij de van-/naar-postcode (leeg = geen match).
  fromLabels: PostcodeLabel[]
  toLabels: PostcodeLabel[]
}

export interface DistanceCacheStatsDTO {
  totaal: number
  /**
   * payroll-werkruimte 18→8 — routes van de primaire provider (Google Maps).
   * De DB-enum-waarde heet uit historische reden nog `MAPBOX`; alleen de
   * DTO-veldnaam en het KPI-label zijn rechtgetrokken, het gedrag niet.
   */
  googleMaps: number
  pdok: number
  handmatig: number
  onbekend: number
  stale30d: number
  stale60d: number
  stale90d: number
}

export interface DistanceCacheRefreshAllResultDTO {
  refreshed: number
  errors: number
}

// ─── Zod schemas ─────────────────────────────────────────────────────────────

const addressSchema = z.object({
  postcode:    z.string().min(4),
  houseNumber: z.string().min(1).max(10),
})

export const distanceLookupSchema = z.object({
  from: addressSchema,
  to:   addressSchema,
  forceRefresh: z.boolean().optional(),
})
export type DistanceLookupInput = z.infer<typeof distanceLookupSchema>

export const distanceLookupBatchSchema = z.object({
  from: addressSchema,
  destinations: z.array(addressSchema).min(1).max(50),
  forceRefresh: z.boolean().optional(),
})
export type DistanceLookupBatchInput = z.infer<typeof distanceLookupBatchSchema>

export const listDistanceCacheSchema = z.object({
  search:   z.string().trim().max(100).optional(),       // matcht op postcode+huisnummer
  source:   z.enum(DISTANCE_CACHE_SOURCE).optional(),
  isStale:  queryBoolean().optional(),
  page:     z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(2000).default(50),
})
export type ListDistanceCacheInput = z.infer<typeof listDistanceCacheSchema>

export const updateDistanceCacheManualSchema = z.object({
  kilometers: z.number().nonnegative().max(99999),
  minutes:    z.number().int().nonnegative().max(99999),
  source:     z.literal('HANDMATIG').default('HANDMATIG').optional(),
})
export type UpdateDistanceCacheManualInput = z.infer<typeof updateDistanceCacheManualSchema>

// ─── Route-cache saneren + open dagen herrekenen (batch-50, FO-403 §3.C) ─────
//
// Eén gedeeld request-schema: de API parset hiermee en het beheer-formulier
// valideert er client-side mee, zodat het form niets kan sturen dat de API
// weigert. Zonder scope draait de sanering over de volledige cache.

const saneringPaarSchema = z.object({
  postcode:    z.string().min(4).max(10),
  houseNumber: z.string().min(1).max(10),
})

export const saneerRouteCacheSchema = z.object({
  /** Default aan: niets wijzigen, alleen rapporteren (spec §9). */
  dryRun: z.boolean().optional().default(true),
  /** Alleen dit adres-paar (beide richtingen) - bv. één klantroute. */
  paar: z.object({ from: saneringPaarSchema, to: saneringPaarSchema }).nullable().optional(),
  /** Alleen routes naar de locatie(s) van deze klant. */
  clientId: z.string().min(1).nullable().optional(),
  /** Open dagen op de geraakte routes meteen herrekenen (veilige FO-068-pad). */
  herberekenen: z.boolean().optional().default(true),
})
export type SaneerRouteCacheInput = z.infer<typeof saneerRouteCacheSchema>

/** Waarom een cache-rij als vervuild is aangemerkt. */
export const CACHE_SANERING_REDENEN = [
  'LANDMIDDEN_COORDINAAT',
  'GEOCODE_AFGEKEURD',
  'GEOCODE_ONBEREIKBAAR',
] as const
export type CacheSaneringReden = (typeof CACHE_SANERING_REDENEN)[number]

export const CACHE_SANERING_REDEN_LABELS: Record<CacheSaneringReden, string> = {
  LANDMIDDEN_COORDINAAT: 'Eindpunt valt op het midden van Nederland',
  GEOCODE_AFGEKEURD:     'Adres wordt door de kwaliteitscontrole afgekeurd',
  GEOCODE_ONBEREIKBAAR:  'Adres kon niet gecontroleerd worden',
}

export interface CacheSaneringRijDTO {
  id: string
  from: { postcode: string; houseNumber: string }
  to: { postcode: string; houseNumber: string }
  kilometers: number
  minutes: number
  source: DistanceCacheSource
  reden: CacheSaneringReden
  /** Welke kant het probleem gaf ('from' | 'to' | 'beide'). */
  kant: 'from' | 'to' | 'beide'
  /** true = HANDMATIG-rij: nooit automatisch overschreven, alleen gerapporteerd. */
  handmatigBehouden: boolean
}

export interface CacheSaneringRapportDTO {
  dryRun: boolean
  /** Aantal cache-rijen dat is onderzocht. */
  onderzocht: number
  /** Vervuilde rijen (incl. de HANDMATIG-rijen die blijven staan). */
  gevonden: CacheSaneringRijDTO[]
  /** Aantal daadwerkelijk geïnvalideerde rijen (0 in dry-run). */
  geinvalideerd: number
  /** HANDMATIG-rijen: blijven staan, moeten herbeoordeeld worden. */
  handmatigGerapporteerd: number
  /** Herberekening van open dagen (null als `herberekenen: false`). */
  herberekening: {
    medewerkers: number
    herrekendeDagen: number
    overgeslagenDagen: number
  } | null
  /** True als het onderzoek op zijn batch-plafond stuitte (niet alles gezien). */
  afgekapt: boolean
  /**
   * Veiligheidsrem: zoveel eindpunten werden afgekeurd dat de geocoder eerder
   * stuk/uitgeput lijkt dan dat de data vervuild is. Er is dan NIETS
   * geïnvalideerd en NIET herberekend - alleen gerapporteerd.
   */
  geocoderVerdacht: boolean
}
